import { ENVIRONMENTS } from "../environment";
import { NodeEnv } from "../trade/campaigns/getCampaigns";
import { Chain } from "viem";
import { arbitrum, base, mainnet, sepolia } from "viem/chains";

export const getRewardChain = (nodeEnv: NodeEnv | ENVIRONMENTS): Chain => {
  const isProd = nodeEnv === "production" || nodeEnv === ENVIRONMENTS.CRON_WORKER;
  return isProd ? (arbitrum as Chain) : (sepolia as Chain);
};

/**
 * Registry of viem chains that can host reward distribution.
 *
 * Additive multichain support: rewards can be funded/claimed on any chain in
 * this set. `getRewardChain(nodeEnv)` still selects the default per-environment
 * chain (Arbitrum in prod, Sepolia otherwise); this registry is what new
 * chain-aware call sites resolve against when they carry an explicit chainId.
 */
export const SUPPORTED_REWARD_CHAINS: readonly Chain[] = [
  arbitrum as Chain,
  mainnet as Chain,
  base as Chain,
  sepolia as Chain,
];

/**
 * The platform's historical/default reward chain (Arbitrum One). Used as the
 * explicit creation-time default for campaigns whose creator specifies no
 * chain — never as a silent read-time fallback.
 */
export const DEFAULT_REWARD_CHAIN_ID = arbitrum.id;

/** Merkl has no testnet deployment: Merkl-distributed rewards live on Arbitrum in every environment. */
export const MERKL_CHAIN_ID = arbitrum.id;

/** Merkl's test token (aglaMerkl) on Arbitrum: the reward token of Merkl test campaigns. Never a vault reward. */
export const MERKL_TEST_TOKEN = { symbol: "aglaMerkl", address: "0xE0688A2FE90d0f93F17f273235031062a210d691" } as const;

/** Where other rewards run on a testnet, Merkl campaigns are Merkl test campaigns (aglaMerkl, `test=true`). */
export const usesMerklTestCampaigns = (nodeEnv: NodeEnv | ENVIRONMENTS): boolean =>
  getRewardChain(nodeEnv).id !== arbitrum.id;

/**
 * Resolve a supported reward chain by its numeric chainId.
 * Returns `undefined` when the chainId is not a supported reward chain — callers
 * decide how to handle that (no silent fallback to a default chain here).
 */
export const getRewardChainById = (chainId: number): Chain | undefined => {
  return SUPPORTED_REWARD_CHAINS.find((chain) => chain.id === chainId);
};

export const getChainNameById = (id: number) => {
  const chain = CHAINS.ALL.find((chain) => chain.id === id);
  return chain ? chain.networkName : "";
};

export const getChainAssetById = (id: number) => {
  const chain = CHAINS.ALL.find((chain) => chain.id === id);
  return chain ? chain.nativeCurrency.symbol : "";
};

export function getChainIcon(network: string) {
  return `https://chain-icons.s3.amazonaws.com/${network}.png`;
}

export function formatChainName(string: string) {
  return string
    .split("-")
    .map((substr) => substr.charAt(0).toUpperCase() + substr.slice(1))
    .join(" ");
}

const REWARD_ADDRESSES: { [chain: number]: Record<string, string> } = {
  [arbitrum.id]: {
    usdt: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    usdc: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
    weth: "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1",
  } as Record<string, string>,
  [mainnet.id]: {
    usdt: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    usdc: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    weth: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
  } as Record<string, string>,
  [base.id]: {
    usdt: "0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2",
    usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
    weth: "0x4200000000000000000000000000000000000006",
  } as Record<string, string>,
  [sepolia.id]: {
    usdt: "0xaA8E23Fb1079EA71e0a56F48a2aA51851D8433D0",
    usdc: "0x4CeEC89F41ba3f0A8C05a0A09ce73E0c76531ABb",
    weth: "0x4CeEC89F41ba3f0A8C05a0A09ce73E0c76531ABb",
  } as Record<string, string>,
};

/**
 * Spinning Wheel Vault Addresses
 *
 * IMPORTANT: These are different from getRewardsSafeModuleAddress()
 * Each environment has its own dedicated vault for Spinning Wheel rewards distribution.
 *
 */
const SPINNING_WHEEL_VAULT_ADDRESSES: { [chain: number]: `0x${string}` } = {
  [arbitrum.id]: "0x5c0d38c2ad5d84db6b4233b87ad5491656513cd8",
  [sepolia.id]: "0xa2919d4692C9D56dC47B72250D8FB0Ed869A4982",
};

/**
 * Cashback Vault Addresses
 *
 * IMPORTANT: These are different from getRewardsSafeModuleAddress() and Spinning Wheel vault
 * Each environment has its own dedicated vault for Cashback rewards distribution.
 *
 */
const CASHBACK_VAULT_ADDRESSES: { [chain: number]: `0x${string}` } = {
  [arbitrum.id]: "0xc17ed168b262f09cddb39aef77034868f697a032", // Production vault
  [sepolia.id]: "0xfeba661620895f4b8ec05840a5ba1fd61baabbb0", // Testnet vault
};

/**
 * Referral Epoch Builder Vault Address
 *
 * Pinned per environment in code (not via an env var) because the previous env-driven
 * setup let a manual `.env.local` edit silently re-point the vault prefix on S3 between
 * two epoch builder runs, orphaning the merkle JSON of already-built epochs.
 *
 * IMPORTANT: this address must match the on-chain rewards safe the referral builder
 * mints epochs against — rotating it requires deploying a new safe and migrating
 * outstanding entitlements, not editing this map.
 */
export function getReferralEpochBuilderVaultAddress(nodeEnv: NodeEnv | ENVIRONMENTS): `0x${string}` {
  switch (nodeEnv) {
    case "production":
    case ENVIRONMENTS.CRON_WORKER:
    case ENVIRONMENTS.BOT_WORKER:
      return "0x492a022b2d3ed9a179868c58651beeffd877914a";
    case "staging":
      return "0xbdceb95845d4f062ec65e9347d573d76d236f4f6";
    default:
      return "FAKE-VAULT-ADDRESS" as `0x${string}`;
  }
}

/**
 * Resolves a token symbol to its ERC20 address on a given reward chain.
 *
 * @deprecated Only resolves the canonical reward tokens hard-coded in
 * `REWARD_ADDRESSES` (USDT/USDC/WETH per chain) and cannot represent arbitrary
 * ERC20 reward tokens or per-chain variants the symbol alone can't disambiguate.
 * Campaign-scoped rewards must use the persisted `rewardTokenAddress` (the source
 * of truth, server-derived on-chain at campaign save) instead of reverse-deriving
 * the address from the symbol. Kept only for resolving the canonical default
 * token (USDT) and the spinning-wheel/cashback/referral flows, which are
 * USDT-denominated. Throws on an unknown symbol/chain rather than returning a
 * silent empty string.
 */
export function tokenSymbolToAddress(tokenSymbol: string, nodeEnv: NodeEnv | ENVIRONMENTS, chainId?: number): string {
  const resolvedChainId = chainId ?? getRewardChain(nodeEnv).id;
  const address = REWARD_ADDRESSES[resolvedChainId]?.[tokenSymbol.toLowerCase()];
  if (address === undefined) {
    throw new Error(
      `tokenSymbolToAddress: no address for token "${tokenSymbol}" on chain ${resolvedChainId}. ` +
        `Only canonical reward tokens are mapped; campaign rewards must use the persisted rewardTokenAddress.`,
    );
  }
  return address;
}

// Reverse mapping: token address to symbol.
// When `chainId` is provided it resolves against that chain's REWARD_ADDRESSES;
// when omitted it preserves the original behavior (default chain via nodeEnv).
export function tokenAddressToSymbol(
  tokenAddress: string,
  nodeEnv: NodeEnv | ENVIRONMENTS,
  chainId?: number,
): string | null {
  const resolvedChainId = chainId ?? getRewardChain(nodeEnv).id;
  const chainAddresses = REWARD_ADDRESSES[resolvedChainId];

  if (!chainAddresses) return null;

  const lowerAddress = tokenAddress.toLowerCase();

  // Find symbol by address (reverse lookup)
  for (const [symbol, address] of Object.entries(chainAddresses)) {
    if (address.toLowerCase() === lowerAddress) {
      return symbol.toUpperCase();
    }
  }

  return null;
}

/**
 * Get Spinning Wheel vault address for current environment
 *
 * Returns the dedicated vault address used for Spinning Wheel rewards distribution.
 * This is different from the general rewards safe module address.
 */
export function getSpinningWheelVaultAddress(nodeEnv: NodeEnv | ENVIRONMENTS): `0x${string}` {
  const currentRewardChain = getRewardChain(nodeEnv);
  const address = SPINNING_WHEEL_VAULT_ADDRESSES[currentRewardChain.id];

  if (!address) {
    throw new Error(`Spinning Wheel vault address not configured for chain ${currentRewardChain.id}`);
  }

  return address;
}

/**
 * Get Cashback vault address for current environment
 *
 * Returns the dedicated vault address used for Cashback rewards distribution.
 * This is different from the general rewards safe module address and Spinning Wheel vault.
 */
export function getCashbackVaultAddress(nodeEnv: NodeEnv | ENVIRONMENTS): `0x${string}` {
  const currentRewardChain = getRewardChain(nodeEnv);
  const address = CASHBACK_VAULT_ADDRESSES[currentRewardChain.id];

  if (!address) {
    throw new Error(`Cashback vault address not configured for chain ${currentRewardChain.id}`);
  }

  return address;
}

/**
 * A CedeRewardsSafe vault funding Cede Arena virtual-balance withdrawals, and the
 * rewards module its epochs and claims go through. `"shared"` is the
 * environment's campaign rewards module ({@link getRewardsSafeModuleAddress});
 * a dedicated module is a CedeRewardsSafeModuleV3+ proxy, which also pays small
 * withdrawals instantly.
 */
export type ArenaWithdrawVault = {
  address: `0x${string}`;
  module: "shared" | `0x${string}`;
};

// Per chain, newest first: the first vault receives new withdrawals, older ones
// stay registered so their requests remain claimable (claims never expire).
// Local dev overrides the vault via LOCAL_ARENA_WITHDRAW_VAULT_ADDRESS — see
// BlockchainConfigService.getArenaWithdrawVaultAddress.
const ARENA_WITHDRAW_VAULTS: { [chain: number]: ArenaWithdrawVault[] } = {
  [arbitrum.id]: [
    { address: "0x6d1767b9933df6fB7d5AB6e36eE8Df65a6454fCB", module: "0x4bA2dFEabB2f84E13de0AC120D7Fc0c6e66aCeDe" },
    { address: "0x4473b0f750b5aa00b3e401e1db141f83c29bcece", module: "shared" },
  ],
  [sepolia.id]: [{ address: "0x0000000000000000000000000000000000000000", module: "shared" }], // TODO: staging / dev arena withdraw vault
};

/**
 * Get the Cede Arena vault new withdrawals are paid from, for the current
 * environment. Lives in code config (versioned + auditable) like the
 * spinning-wheel / cashback vaults; local dev overrides it via env.
 */
export function getArenaWithdrawVaultAddress(nodeEnv: NodeEnv | ENVIRONMENTS): `0x${string}` {
  return getArenaWithdrawVaultsForChain(getRewardChain(nodeEnv).id)[0].address;
}

/** Every Arena withdraw vault registered on a chain, newest first. */
export function getArenaWithdrawVaultsForChain(chainId: number): [ArenaWithdrawVault, ...ArenaWithdrawVault[]] {
  const [active, ...older] = ARENA_WITHDRAW_VAULTS[chainId] ?? [];

  if (!active) {
    throw new Error(`Arena withdraw vault address not configured for chain ${chainId}`);
  }

  return [active, ...older];
}

/**
 * The registry entry of a persisted `CedeArenaWithdrawRequest.vaultAddress`, or
 * `undefined` when the address is absent from the registry (a vault deployed
 * without updating it) — callers surface that rather than guessing a module or
 * chain for a payout.
 */
export function resolveArenaWithdrawVault(
  vaultAddress: string,
): { chain: Chain; vault: ArenaWithdrawVault } | undefined {
  const normalized = vaultAddress.toLowerCase();
  for (const [chainId, vaults] of Object.entries(ARENA_WITHDRAW_VAULTS)) {
    const vault = vaults.find((entry) => entry.address.toLowerCase() === normalized);
    if (!vault) continue;
    const chain = getRewardChainById(Number(chainId));
    if (!chain) {
      throw new Error(`Arena withdraw vault ${vaultAddress} is registered on chain ${chainId}, not a reward chain`);
    }
    return { chain, vault };
  }
  return undefined;
}

/**
 * Inverse of {@link getArenaWithdrawVaultAddress}: the chain a persisted
 * `CedeArenaWithdrawRequest.vaultAddress` was paid on, `undefined` when the
 * vault is unregistered.
 */
export function resolveArenaWithdrawChain(vaultAddress: string): Chain | undefined {
  return resolveArenaWithdrawVault(vaultAddress)?.chain;
}

export const CHAINS = {
  ALL: [
    {
      blockchainName: "Bitcoin",
      networkName: "BTC",
      // srcImg: require("../images/blockchain_logos/btc.svg"),
      alt: "icon-bitcoin",
      id: null,
      regexVerification: /^(?:[13]{1}[a-km-zA-HJ-NP-Z1-9]{26,33}|bc1[a-z0-9]{39,59})$/,
      nativeCurrency: {
        name: "Bitcoin",
        symbol: "BTC",
        decimals: 18,
      },
    },
    {
      blockchainName: "Ethereum",
      networkName: "ETH",
      // srcImg: require("../images/blockchain_logos/ether.svg"),
      alt: "icon-ethereum",
      id: 1,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "Ether",
        symbol: "ETH",
        decimals: 18,
      },
      rpcUrl: "https://eth.public-rpc.com",
    },
    {
      blockchainName: "BSC",
      networkName: "BSC",
      // srcImg: require("../images/blockchain_logos/bsc.svg"),
      alt: "icon-bsc",
      id: 56,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "BSC",
        symbol: "BSC",
        decimals: 18,
      },
      rpcUrl: "https://bsc-dataseed.binance.org/",
    },
    {
      blockchainName: "Polygon",
      networkName: "POLY",
      // srcImg: require("../images/blockchain_logos/polygon.svg"),
      alt: "icon-matic",
      id: 137,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "Matic",
        symbol: "MATIC",
        decimals: 18,
      },
      rpcUrl: "https://polygon-rpc.com/",
    },
    {
      blockchainName: "Avalanche",
      networkName: "AVA",
      // srcImg: require("../images/blockchain_logos/avalanche.svg"),
      alt: "icon-avax",
      id: 43114,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "AVAX",
        symbol: "AVAX",
        decimals: 18,
      },
      rpcUrl: "https://api.avax.network/ext/bc/C/rpc",
    },
    // {
    //   blockchainName: "Avalanche XP",
    //   networkName: "AVA",
    //   srcImg: require("../images/blockchain_logos/avalanche.svg"),
    //   alt: "icon-avax",
    //   id: null,
    //   regexVerification: ^([XPC]|[a-km-zA-HJ-NP-Z1-9]{36,72})-[a-zA-Z]{1,83}1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{38}$\n,
    //   nativeCurrency: {
    //     name: "AVAX",
    //     symbol: "AVAX",
    //     decimals: 18,
    //   },
    // },
    {
      blockchainName: "Optimism",
      networkName: "OPT",
      // srcImg: require("../images/blockchain_logos/optimism.svg"),
      alt: "icon-ethereum",
      id: 10,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "Ether",
        symbol: "ETH",
        decimals: 18,
      },
      rpcUrl: "https://mainnet.optimism.io",
    },

    {
      blockchainName: "Arbitrum One",
      networkName: "ARB",
      // srcImg: require("../images/blockchain_logos/arbitrum.svg"),
      alt: "icon-ethereum",
      id: 42161,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "AEther",
        symbol: "AETH",
        decimals: 18,
      },
      rpcUrl: "https://arb1.arbitrum.io/rpc",
    },
    {
      blockchainName: "Base",
      networkName: "BASE",
      alt: "icon-ethereum",
      id: 8453,
      regexVerification: /^0x[a-fA-F0-9]{40}$/,
      nativeCurrency: {
        name: "Ether",
        symbol: "ETH",
        decimals: 18,
      },
      rpcUrl: "https://mainnet.base.org",
    },
  ],
};
